import { useMemo, useRef } from "react";
import { useTranslate, useNotification } from "@refinedev/core";
import { useQueryClient } from "@tanstack/react-query";

import { usePaginatedTable, useQueryParams } from "@shared/hooks";
import { ModalRef } from "@shared/components";
import { AgreementIntegrationConfig } from "@modules/erpIntegrations/types";
import { useDeleteConfigMutation } from "@modules/erpIntegrations/services/mutations";
import {
  listConfigsQueryKey,
  useListIntegrationsQuery,
} from "@modules/erpIntegrations/services/queries";
import { useListAgreementsQuery } from "@modules/agreements/services/queries/listAgreements";

export function useConfigsTab() {
  const translate = useTranslate();
  const { open } = useNotification();
  const queryClient = useQueryClient();
  const { getQueryParams } = useQueryParams();

  const createModalRef = useRef<ModalRef>(null);
  const editModalRef = useRef<ModalRef>(null);

  const params = getQueryParams();
  const filters = {
    ...(params.configs_agreement ? { agreement: params.configs_agreement } : {}),
    ...(params.configs_integration
      ? { integration: params.configs_integration }
      : {}),
    ...(params.configs_plan_map_option
      ? { plan_map_option: params.configs_plan_map_option }
      : {}),
  };

  const { rows, loading, refetch, ...tableProps } =
    usePaginatedTable<AgreementIntegrationConfig>({
      resource: "v1/agreement_integration_config_admin",
      filters,
      sorters: { ordering: "-updated_at" },
    });

  const { data: integrations = [] } = useListIntegrationsQuery({ perPage: 100 });

  const agreementIds = useMemo(
    () => Array.from(new Set(rows.map((row) => row.agreement))).filter(Boolean),
    [rows]
  );

  const { data: agreementsForRows = [] } = useListAgreementsQuery(
    { ids: agreementIds, perPage: agreementIds.length },
    { enabled: agreementIds.length > 0 }
  );

  const agreementNameById = useMemo(
    () => new Map(agreementsForRows.map((agreement) => [agreement.id, agreement.name])),
    [agreementsForRows]
  );

  const deleteMutation = useDeleteConfigMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: listConfigsQueryKey,
        refetchType: "all",
      });
      open?.({
        type: "success",
        message: translate("v1/agreement_integration_config_admin.success.delete"),
      });
      refetch();
    },
  });

  const handleDelete = async (id: number) => {
    if (
      !window.confirm(
        translate("v1/agreement_integration_config_admin.confirm_delete")
      )
    ) {
      return;
    }
    await deleteMutation.mutateAsync(id);
  };

  return {
    rows,
    loading,
    tableProps,
    integrations,
    agreementNameById,
    createModalRef,
    editModalRef,
    refetch,
    translate,
    handleDelete,
  };
}
